/**
 * V13.14.6 / V41 — Learner Billing page (/learner/billing)
 *
 * Customer-facing ledger. Rules (V41): item names read like the invoice line
 * ("Prenatal Yoga Trial Class"), no gateway ids / order ids / SKUs on screen,
 * and the list is mobile-first (stacked rows — no horizontally cropped table).
 * Backend self-heals missing enrolment payments by pulling from OPS on load;
 * "Refresh" forces that pull.
 */
import { TalkToTeamButton } from '../../components/learner/TalkToTeamButton';
import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, CreditCard, Download, ExternalLink, Receipt, RefreshCw } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const STATUS_PILL = {
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending:   'bg-amber-50 text-amber-700 border-amber-200',
  failed:    'bg-rose-50 text-rose-700 border-rose-200',
  refunded:  'bg-slate-100 text-slate-700 border-slate-200',
};
const STATUS_LABEL = { completed: 'Paid', pending: 'Awaiting payment', failed: 'Failed', refunded: 'Refunded' };

const money = (amount, currency) => {
  const n = Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return (currency || 'INR') === 'INR' ? `₹${n}` : `${currency} ${n}`;
};

export default function MyBillingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await api.get('/api/payment/history', { params: { limit: 100, ...(refresh ? { refresh: 1 } : {}) } });
      setData(res.data);
      if (refresh) {
        const n = res.data?.ops_sync?.inserted || 0;
        toast.success(n > 0 ? `${n} payment${n > 1 ? 's' : ''} added to your history.` : 'Your billing history is up to date.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load billing history');
      if (!refresh) setData({ transactions: [], total_spent: 0, count: 0 });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const downloadReceipt = async (tx) => {
    const external = tx.invoice_url || tx.receipt_url;
    if (external) {
      window.open(external, '_blank', 'noopener');
      return;
    }
    setDownloadingId(tx.id);
    try {
      const ref = tx.payment_id || tx.id;
      const res = await api.get(`/api/payment/my-payments/${ref}/receipt`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SoulSanskar-Receipt-${(tx.completed_at || tx.created_at || '').slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Receipt unavailable';
      toast.error(typeof msg === 'string' ? msg : 'Receipt unavailable');
    }
    setDownloadingId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="my-billing-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const txs = data?.transactions || [];
  const totalCompleted = txs.filter(t => t.status === 'completed').length;
  const totals = Object.entries(data?.totals_by_currency || {});

  return (
    <div className="space-y-6 max-w-3xl" data-testid="my-billing-page">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center shrink-0">
            <Receipt className="h-5 w-5 text-rose-600" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight">My Billing</h1>
            <p className="text-sm text-muted-foreground">Your payment history and downloadable receipts.</p>
          </div>
        </div>
        <Button size="sm" variant="ghost" className="h-8 gap-1.5 text-xs shrink-0" onClick={() => load(true)}
          disabled={refreshing} data-testid="my-billing-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-primary/5 border-primary/10">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground">Total Paid</p>
            <p className="text-2xl font-semibold mt-1" data-testid="my-billing-total">
              {money(data?.total_spent, data?.currency)}
            </p>
            {totals.length > 1 && (
              <p className="text-[11px] text-muted-foreground mt-1" data-testid="my-billing-total-breakdown">
                {totals.map(([ccy, amt]) => money(amt, ccy)).join(' · ')}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground">Successful Payments</p>
            <p className="text-2xl font-semibold mt-1" data-testid="my-billing-count">{totalCompleted}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {txs.length === 0 ? (
            <div className="text-center py-14 px-4">
              <CreditCard className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm font-medium">No payments yet</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                Once you book a class or enrol in a program, your payments and receipts will appear here.
              </p>
            </div>
          ) : (
            <ul className="divide-y" data-testid="my-billing-list">
              {txs.map(tx => {
                const date = tx.completed_at || tx.created_at;
                const canDownload = tx.status === 'completed';
                const hasLink = Boolean(tx.invoice_url || tx.receipt_url);
                return (
                  <li key={tx.id} className="flex items-center gap-3 px-4 py-3.5" data-testid={`my-billing-row-${tx.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug" data-testid={`my-billing-item-${tx.id}`}>{tx.purpose_label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {date ? formatDate(date) : '—'}
                        {tx.invoice_number && <span className="ml-2">· Invoice {tx.invoice_number}</span>}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold" data-testid={`my-billing-amount-${tx.id}`}>{money(tx.amount, tx.currency)}</p>
                      <Badge variant="outline" className={`${STATUS_PILL[tx.status] || 'bg-slate-50 text-slate-700'} text-[10px] mt-1`}
                        data-testid={`my-billing-status-${tx.id}`}>
                        {STATUS_LABEL[tx.status] || tx.status}
                      </Badge>
                    </div>
                    <div className="shrink-0 w-9 flex justify-end">
                      {canDownload && (
                        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => downloadReceipt(tx)}
                          disabled={downloadingId === tx.id} data-testid={`my-billing-download-${tx.id}`}
                          title={hasLink ? 'Open invoice' : 'Download receipt (PDF)'} aria-label="Receipt">
                          {downloadingId === tx.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : hasLink ? <ExternalLink className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground px-1" data-testid="my-billing-help">
        A payment missing here? Tap Refresh — it can take a few minutes to appear after you pay. For a GST tax invoice,
        request a callback — our team will call you within 24 hours (except Sunday).
      </p>
      <TalkToTeamButton category="payment" message="Billing: I need a GST tax invoice / a receipt is missing." label="Request a callback" testId="billing-callback-btn" />
      <p className="hidden">
      </p>
    </div>
  );
}
